/**
 * Which locations a placement on this product's style can sit at.
 *
 * ONE source for two pickers (docs/design-philosophy.md principle 2): the
 * Add-placement wizard offers these locations for a new placement, and the
 * placement location picker (always available since Jake + Mike, 2026-10-21)
 * offers the same ones for an existing placement, minus the locations other
 * placements on the product already hold -- one decoration per location
 * (the backend's ux_placements_cp_zone_active enforces it).
 *
 * The style's live Artifi views are the authority. When they are unknown
 * (not fetched yet, fetch failed, Artifi not configured) the pickers fail
 * open to the placement-zone catalog, as isZoneValidForSku does.
 */
import { liveViewCodeForSlug } from "@/utils/placementZoneToViewCode";

/** The views, when they are an authority; otherwise null. */
export function skuViewsAuthority(skuViews) {
    return Array.isArray(skuViews) && skuViews.length ? skuViews : null;
}

/**
 * The wizard's option list, normalized to `{ value, label }`.
 *   - views known: value = view code (what the bind sends as `view_code`)
 *   - views unknown: value = zone id (sent as `placement_zone_id`), and the
 *     catalog row rides along as `zone`
 */
export function zoneOptionsForSku(skuViews, zones) {
    const views = skuViewsAuthority(skuViews);
    if (views) {
        return views
            .filter((v) => v && typeof v.code === "string")
            .map((v) => ({ value: v.code, label: v.display_name || v.code }));
    }
    return (Array.isArray(zones) ? zones : []).map((z) => ({
        value: z.id,
        label: z.display_name,
        zone: z,
    }));
}

function sameRow(a, b) {
    if (a === b) return true;
    const ida = a?.id ?? a?._tempId;
    const idb = b?.id ?? b?._tempId;
    return ida != null && String(ida) === String(idb);
}

/**
 * The picker's state for one placement.
 *
 * @returns {{ mode: "view"|"zone", current: string|null,
 *             options: Array<{value: string, label: string}> }}
 *   `current` is the option value of the placement's own location, or null
 *   when the style does not carry it (a SKU swap stranded it). `options`
 *   always includes the current location and never one another placement
 *   on the product holds.
 */
export function placementLocationChoices({ placement, placements, skuViews, zones }) {
    const views = skuViewsAuthority(skuViews);
    const others = (Array.isArray(placements) ? placements : []).filter(
        (p) => p && !sameRow(p, placement),
    );
    const all = zoneOptionsForSku(skuViews, zones).map((o) => ({
        value: String(o.value),
        label: o.label,
    }));

    let current;
    let taken;
    if (views) {
        const codeOf = (p) => liveViewCodeForSlug(p?.placement_zone_slug, views);
        current = codeOf(placement);
        taken = new Set(others.map(codeOf).filter((c) => c != null));
    } else {
        const idOf = (p) =>
            p?.placement_zone_id != null ? String(p.placement_zone_id) : null;
        current = idOf(placement);
        taken = new Set(others.map(idOf).filter((z) => z != null));
    }
    return {
        mode: views ? "view" : "zone",
        current: current ?? null,
        options: all.filter((o) => o.value === current || !taken.has(o.value)),
    };
}
